import { CardContainer, CardKey } from '../../StyledComponents/Card';
import CondorSpinner from '../CondorSpinner';
import { IconButton } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useGetDeviceTagsQuery } from '../../gql/generated';
import { useState, useEffect } from 'react';

interface CondorStandardTagsProps {
  purpleTag: {
    tagId: string;
    tagName: string;
  };
  yellowTag: {
    tagId: string;
    tagName: string;
  };
}

const PAGE_SIZE = 1000;

export const CondorStandardTags = (props: CondorStandardTagsProps) => {
  const { purpleTag, yellowTag } = props;

  const [purpleData, setPurpleData] = useState<any>(null);
  const [yellowData, setYellowData] = useState<any>(null);

  const { data: purpleDataInitial, fetchMore: fetchMorePurple } =
    useGetDeviceTagsQuery({
      variables: {
        input: {
          tagId: purpleTag.tagId,
          limit: PAGE_SIZE,
        },
      },
      skip: !purpleTag.tagId,
    });

  const { data: yellowDataInitial, fetchMore: fetchMoreYellow } =
    useGetDeviceTagsQuery({
      variables: {
        input: {
          tagId: yellowTag.tagId,
          limit: PAGE_SIZE,
        },
      },
      skip: !yellowTag.tagId,
    });

  // Fetch all purple tag data
  useEffect(() => {
    if (!purpleDataInitial || !purpleTag.tagId) return;

    const fetchAllPurpleData = async () => {
      let allResults = [...(purpleDataInitial.getDeviceTags?.results || [])];
      let nextCursor = purpleDataInitial.getDeviceTags?.next;
      const count = purpleDataInitial.getDeviceTags?.count;

      while (nextCursor) {
        try {
          const result = await fetchMorePurple({
            variables: {
              input: {
                tagId: purpleTag.tagId,
                limit: PAGE_SIZE,
                next: nextCursor,
              },
            },
          });

          const newResults = result.data?.getDeviceTags?.results || [];
          allResults = [...allResults, ...newResults];
          nextCursor = result.data?.getDeviceTags?.next;
        } catch (error) {
          console.error('Error fetching more purple data:', error);
          break;
        }
      }

      setPurpleData({
        getDeviceTags: {
          results: allResults,
          count: count,
          loading: false,
        },
      });
    };

    fetchAllPurpleData();
  }, [purpleDataInitial, purpleTag.tagId, fetchMorePurple]);

  // Fetch all yellow tag data
  useEffect(() => {
    if (!yellowDataInitial || !yellowTag.tagId) return;

    const fetchAllYellowData = async () => {
      let allResults = [...(yellowDataInitial.getDeviceTags?.results || [])];
      let nextCursor = yellowDataInitial.getDeviceTags?.next;
      const count = yellowDataInitial.getDeviceTags?.count;

      while (nextCursor) {
        try {
          const result = await fetchMoreYellow({
            variables: {
              input: {
                tagId: yellowTag.tagId,
                limit: PAGE_SIZE,
                next: nextCursor,
              },
            },
          });

          const newResults = result.data?.getDeviceTags?.results || [];
          allResults = [...allResults, ...newResults];
          nextCursor = result.data?.getDeviceTags?.next;
        } catch (error) {
          console.error('Error fetching more yellow data:', error);
          break;
        }
      }

      setYellowData({
        getDeviceTags: {
          results: allResults,
          count: count,
          loading: false,
        },
      });
    };

    fetchAllYellowData();
  }, [yellowDataInitial, yellowTag.tagId, fetchMoreYellow]);

  const purpleDeviceCount = purpleData?.getDeviceTags?.results?.length ?? 0;
  const yellowDeviceCount = yellowData?.getDeviceTags?.results?.length ?? 0;
  const totalDevices = purpleDeviceCount + yellowDeviceCount;

  const purplePercentage =
    totalDevices > 0
      ? ((purpleDeviceCount / totalDevices) * 100).toFixed(1)
      : '0.0';

  const yellowPercentage =
    totalDevices > 0
      ? ((yellowDeviceCount / totalDevices) * 100).toFixed(1)
      : '0.0';

  const handleDownload = (
    tag: { tagId: string; tagName: string },
    data: any,
  ) => {
    const serialNumbers = data?.getDeviceTags?.results
      ?.map((dt: any) => dt?.serialNumber)
      .filter((sn: any): sn is string => !!sn);

    if (!serialNumbers || serialNumbers.length === 0) {
      console.error('No serial numbers found for tagId: ' + tag.tagId);
      return;
    }

    const csvContent = ['Serial Number', ...serialNumbers].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${tag.tagName}-serial-numbers.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ flexBasis: '20%' }}></div>
      <div style={{ flexBasis: '60%' }}>
        <CardContainer>
          {!purpleData ||
          purpleData.loading ||
          !yellowData ||
          yellowData.loading ? (
            <CondorSpinner />
          ) : (
            <div>
              <div
                style={{
                  display: 'flex',
                  marginBottom: '16px',
                  alignItems: 'center',
                }}
              >
                <div style={{ flexBasis: '25%' }}>
                  <CardKey>Purple Tag</CardKey>
                </div>
                <div style={{ flexBasis: '25%', fontSize: '1rem' }}>
                  {purpleTag.tagName}
                </div>
                <div
                  style={{
                    flexBasis: '35%',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: '#6b46c1',
                  }}
                >
                  {purpleDeviceCount} devices ({purplePercentage}%)
                </div>
                <div style={{ flexBasis: '15%', textAlign: 'right' }}>
                  <IconButton
                    onClick={() => handleDownload(purpleTag, purpleData)}
                    aria-label="download-purple-tag-serial-numbers"
                    title="Download Purple Tag Serial Numbers"
                    disabled={!purpleData?.getDeviceTags?.results?.length}
                  >
                    <Download />
                  </IconButton>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flexBasis: '25%' }}>
                  <CardKey>Yellow Tag</CardKey>
                </div>
                <div style={{ flexBasis: '25%', fontSize: '1rem' }}>
                  {yellowTag.tagName}
                </div>
                <div
                  style={{
                    flexBasis: '35%',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: '#d69e2e',
                  }}
                >
                  {yellowDeviceCount} devices ({yellowPercentage}%)
                </div>
                <div style={{ flexBasis: '15%', textAlign: 'right' }}>
                  <IconButton
                    onClick={() => handleDownload(yellowTag, yellowData)}
                    aria-label="download-yellow-tag-serial-numbers"
                    title="Download Yellow Tag Serial Numbers"
                    disabled={!yellowData?.getDeviceTags?.results?.length}
                  >
                    <Download />
                  </IconButton>
                </div>
              </div>
            </div>
          )}
        </CardContainer>
      </div>
      <div style={{ flexBasis: '20%' }}></div>
    </div>
  );
};
